import { createSlice } from '@reduxjs/toolkit';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import { setVehicleByIndex } from 'app/store/slices/forms';
import { errorHandler } from 'app/utils/errorHandler';
import { orderBy, cloneDeep } from 'lodash';

export const DETRAN_KEYS = [
  'cdCategoriaVeiculo',
  'cdClassificacaoVeiculo',
  'cdMarca',
  'cdModelo',
  'cdModeloVeiculo',
  'cdMunicipio',
  'cdSubSistema',
  'cdTipoAcessibilidade',
  'cdTipoCombustivel',
  'cdTipoVeiculo',
  'classificacaoVeiculo',
  'deChassi',
  'deCpfCnpjProprietario',
  'deObservacao',
  'dePlaca',
  'deRenavam',
  'deUtilizacao',
  'modelo',
  'municipio',
  'nmProprietario',
  'nuAnoFabricacao',
  'nuAnoIpva',
  'nuAnoModelo',
  'qtCapacidade',
  'subSistema',
  'tipoCombustivel',
  'tipoVeiculo',
  'vlPotenciaMotor'
]

const initialState = {
  vehicleAllPlates: [],
  vehiclePlate: [],
  vehicleType: [],
  vehicleCategories: [],
  vehicleModels: [],
  vehicleBrands: [],
  subSystems: [],
  accessibilityType: [],
  fuelType: [],
  vehicleClassification: [],
  inspectedVehicles: [],
  selectedVehicles: [],
  licenseVehicleDocuments: [],
};

const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setVehicleAllPlates(state, action) {
      state.vehicleAllPlates = action.payload;
    },
    clearVehicleAllPlates(state, action) {
      state.vehicleAllPlates = initialState.vehicleAllPlates;
    },
    setVehiclePlateByCompany(state, action) {
      state.vehiclePlate = action.payload;
    },
    clearVehiclePlateByCompany(state, action) {
      state.vehiclePlate = initialState.vehiclePlate;
    },
    setVehicleTypes(state, action) {
      state.vehicleType = action.payload;
    },
    clearVehicleTypes(state, action) {
      state.vehicleType = initialState.vehicleType;
    },
    setVehicleCategories(state, action) {
      state.vehicleCategories = action.payload;
    },
    clearVehicleCategories(state, action) {
      state.vehicleCategories = initialState.vehicleCategories;
    },
    setVehicleBrands(state, action) {
      state.vehicleBrands = action.payload;
    },
    clearVehicleBrands(state, action) {
      state.vehicleBrands = initialState.vehicleBrands;
    },
    setVehicleModels(state, action) {
      state.vehicleModels = action.payload;
    },
    clearVehicleModels(state, action) {
      state.vehicleModels = initialState.vehicleModels;
    },
    setSubSystems(state, action) {
      state.subSystems = action.payload;
    },
    clearSubSystems(state, action) {
      state.subSystems = initialState.subSystems;
    },
    setAccessibilityType(state, action) {
      state.accessibilityType = action.payload;
    },
    clearAccessibilityType(state, action) {
      state.accessibilityType = initialState.accessibilityType;
    },
    setFuelType(state, action) {
      state.fuelType = action.payload;
    },
    clearFuelType(state, action) {
      state.fuelType = initialState.fuelType;
    },
    setVehicleClassification(state, action) {
      state.vehicleClassification = action.payload;
    },
    clearVehicleClassification(state, action) {
      state.vehicleClassification = initialState.vehicleClassification;
    },
    setInspectedVehicles(state, action) {
      state.inspectedVehicles = action.payload;
    },
    clearInspectedVehicles(state, action) {
      state.inspectedVehicles = initialState.inspectedVehicles;
    },
    setSelectedVehicles(state, action) {
      state.selectedVehicles = action.payload;
    },
    clearSelectedVehicles(state, action) {
      const selectedVehicle = action.payload;
      if (selectedVehicle) {
        const tempSelectedVehicles = cloneDeep(state.selectedVehicles);
        state.selectedVehicles = tempSelectedVehicles.filter(
          vehicle => vehicle.cdVeiculo !== selectedVehicle.cdVeiculo
        );
      } else {
        state.selectedVehicles = initialState.selectedVehicles;
      }
    },
    setLicenseVehicleDocuments(state, action) {
      state.licenseVehicleDocuments = action.payload;
    },
    clearLicenseVehicleDocuments(state, action) {
      state.licenseVehicleDocuments = initialState.licenseVehicleDocuments;
    },
  },
});

export const fetchVehicleAllPlates = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getAllVehiclesPlate();
    dispatch(setVehicleAllPlates(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehiclePlate = () => async dispatch => {
  try {
    const response = await vehiclesEndpoint.getVehiclesPlatesByCompany();
    if (response.status === 200 && response.data) {
      dispatch(setVehiclePlateByCompany(response.data));
    }
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleByPlate = ({ dePlate, vehicleIndex }) => async dispatch => {
  try {
    const response = await vehiclesEndpoint.getVehiclesByPlate(dePlate);
    if (response.status === 200 && response.data) {
      const data = response.data;

      const transformResponse = {
        // cdVistoriaVeiculo: 123,
        // cdVistoria: 1,
        // dtVistoria: 'Aug 25, 2020 10:52:11 AM',
        // tpSituacao: 'A',
        cdVeiculo: data.cdVeiculo,
        veiculo: { ...response.data },
      };

      dispatch(setVehicleByIndex({ data: transformResponse, vehicleIndex }));
    }
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleTypes = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehicleType();
    dispatch(setVehicleTypes(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleCategories = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehicleCategories();
    dispatch(setVehicleCategories(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleModels = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehicleModels();
    dispatch(setVehicleModels(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleBrands = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehicleBrands();
    dispatch(setVehicleBrands(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchAccessibilityType = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getAccessibilityType();
    dispatch(setAccessibilityType(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchSubSystems = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getSubSystems();
    dispatch(setSubSystems(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchFuelType = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getFuelType();
    dispatch(setFuelType(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchVehicleClassification = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehicleClassification();
    dispatch(setVehicleClassification(response.data));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchInspectedVehicles = () => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getInspectedVehicles();
    const alphabeticallyOrderedData = orderBy(response.data, ['dePlaca'], ['asc']);
    dispatch(setInspectedVehicles(alphabeticallyOrderedData));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchInspectedVehiclesByDocument = document => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getInspectedVehiclesByDocument(document);
    const alphabeticallyOrderedData = orderBy(response.data, ['dePlaca'], ['asc']);
    dispatch(setInspectedVehicles(alphabeticallyOrderedData));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchInspectedVehiclesByDocumentPaginated = params => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getInspectedVehiclesByDocumentPaginated(params);
    const alphabeticallyOrderedData = orderBy(response.data.content, ['dePlaca'], ['asc']);
    dispatch(setInspectedVehicles(alphabeticallyOrderedData));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const fetchLicenseVehicleDocuments = cdServico => async dispatch => {
  try {
    let response = [];
    response = await vehiclesEndpoint.getVehiclesDocuments(cdServico);
    dispatch(setLicenseVehicleDocuments(response.data || []));
  } catch (error) {
    errorHandler(error.response?.status, error.response?.data?.message);
  }
};

export const {
  setVehiclePlateByCompany,
  clearVehiclePlateByCompany,
  setVehicleTypes,
  clearVehicleTypes,
  setVehicleCategories,
  clearVehicleCategories,
  setVehicleBrands,
  clearVehicleBrands,
  setVehicleModels,
  clearVehicleModels,
  setAccessibilityType,
  clearAccessibilityType,
  setSubSystems,
  clearSubSystems,
  setFuelType,
  clearFuelType,
  setVehicleClassification,
  clearVehicleClassification,
  setVehicleAllPlates,
  clearVehicleAllPlates,
  setInspectedVehicles,
  clearInspectedVehicles,
  setSelectedVehicles,
  clearSelectedVehicles,
  setLicenseVehicleDocuments,
  clearLicenseVehicleDocuments,
} = vehicleSlice.actions;

export default vehicleSlice;
